// Tailwind v4 は色を oklch / color-mix(in oklab, ...) で出すが、html2canvas@1 は
// どちらも解釈できず throw する。pro 版はこれらに対応しているのでこちらを使う
import html2canvas from "html2canvas-pro";
import Constants from "common/constant";

// お問い合わせに添える用途なので原寸は不要。長辺をこの幅に収める
const CAPTURE_MAX_WIDTH = 1280;
const CAPTURE_QUALITY = 0.7;

// 報告したいのは下の画面なので、かぶせている層は写さない。
// Dialog は MuiModal-root、ヘルプパネルは MuiBackdrop-root（閉じる途中の
// フェード中でも DOM に残る）。これでダイアログを出したあとに撮っても下の画面が残る
const IGNORED_SELECTOR = ".MuiModal-root, .MuiBackdrop-root, .MuiPopover-root, .Toastify";

const LOCAL_HOSTNAMES = ["localhost", "127.0.0.1", "::1", "[::1]"];

/**
 * CORS ヘッダを返さない画像を通すための中継 URL。使わないときは undefined。
 *
 * ライブラリ側が new URL() で検証するため絶対 URL が要り、さらに localhost の
 * 中継は拒否される。ローカル開発では Constants.apiHost が "" （vite の同一
 * オリジン中継）なので中継なしで撮る。画像は useCORS で直接読みにいく。
 */
const captureProxyUrl = (): string | undefined => {
  if (!Constants.apiHost) return undefined;
  try {
    const url = new URL(`${Constants.apiHost}/capture/proxy`, window.location.origin);
    return LOCAL_HOSTNAMES.includes(url.hostname) ? undefined : url.href;
  } catch {
    return undefined;
  }
};

// 読み込みが終わっていない画像があると、その部分が欠けたまま撮れてしまう。
// 待ちすぎても仕方がないので上限を設けて先に進む
const ASSET_READY_TIMEOUT_MS = 3000;

const waitForAssets = async () => {
  const images = [...document.images].filter((img) => !img.complete);
  const pending: Promise<unknown>[] = images.map(
    (img) =>
      new Promise((resolve) => {
        img.addEventListener("load", resolve, { once: true });
        img.addEventListener("error", resolve, { once: true });
      }),
  );
  if (document.fonts) pending.push(document.fonts.ready);
  if (pending.length === 0) return;
  await Promise.race([
    Promise.all(pending),
    new Promise((resolve) => setTimeout(resolve, ASSET_READY_TIMEOUT_MS)),
  ]);
};

/**
 * いま表示している画面を JPEG の data URL で返す。
 *
 * Babylon の canvas は preserveDrawingBuffer: true で作られているため一緒に写る
 * （features/courses/hook.ts の useCanvas）。
 * MUI のダイアログとトーストは除くので、開いた状態で呼んでも下の画面が撮れる。
 * 添付は必須ではないので、失敗したときは握りつぶして null を返す。
 */
export const captureScreen = async (): Promise<string | null> => {
  try {
    await waitForAssets();
    const proxy = captureProxyUrl();
    const canvas = await html2canvas(document.body, {
      ...(proxy ? { proxy } : {}),
      useCORS: true,
      scale: Math.min(1, CAPTURE_MAX_WIDTH / window.innerWidth),
      // 既定では body の scrollHeight 分を撮る。body は overflow:hidden でも
      // 中身に引きずられて scrollHeight が伸びる（ユーザー選択から入った直後など）ため、
      // 見えている範囲だけを指定しないと縦長の画像になる
      width: window.innerWidth,
      height: window.innerHeight,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      ignoreElements: (element) => element.matches(IGNORED_SELECTOR),
      logging: false,
    });
    return canvas.toDataURL("image/jpeg", CAPTURE_QUALITY);
  } catch (error) {
    console.error("Failed to capture screen:", error);
    return null;
  }
};
